// Blog post catalog — structured content rendered by src/pages/BlogPost.tsx.
// Each post is intentionally long-form (~700–900 words) with real internal
// links to /books and /inquire (the site's booking/contact page) so it
// contributes to topical authority AND funnels readers toward a next step.

export type BlogBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "quote"; text: string; cite?: string }
  | { type: "cta"; label: string; to: string; kind?: "primary" | "ghost" };

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  category: "Leadership" | "Speaking" | "Books";
  readMinutes: number;
  publishedISO: string; // YYYY-MM-DD
  updatedISO?: string;
  eyebrow: string;
  hook: string; // shown above the H1
  body: BlogBlock[];
  keywords: string[];
}

const CTA_INQUIRE: BlogBlock = {
  type: "cta",
  label: "Work With Dr. Sully",
  to: "/inquire",
  kind: "primary",
};

const CTA_BOOKS: BlogBlock = {
  type: "cta",
  label: "Browse the Books",
  to: "/books",
  kind: "ghost",
};

export const BLOG_POSTS: BlogPost[] = [
  // ============================ LEADERSHIP ================================
  {
    slug: "leading-through-change-makechange-playbook",
    title: "Leading Through Change: The MAKECHANGE Playbook for CEOs",
    description:
      "A practical framework for CEOs and founders leading their organization through change without burning trust, talent, or momentum.",
    category: "Leadership",
    readMinutes: 7,
    publishedISO: "2026-07-15",
    eyebrow: "Leadership",
    hook: "Change is the tax leaders pay for growth. Most pay it badly.",
    keywords: [
      "change management for CEOs",
      "how to lead organizational change",
      "MAKECHANGE framework",
      "executive change leadership",
    ],
    body: [
      { type: "p", text: "Every founder I coach reaches the same intersection: the thing that got the company here won't get it there. The team that built the first ten million is not the team that builds the next hundred. The founder's own operating system — the reflexes, the meetings, the mental models — has to be rewritten in public, while the plane is in the air, without losing altitude." },
      { type: "p", text: "That's leading through change. And most executives try to do it with willpower and a Notion doc. It doesn't work. What works is a framework — one repeatable enough that your team can run it without you in the room." },
      { type: "h2", text: "Why change initiatives fail" },
      { type: "p", text: "In 20+ years of coaching executives across healthcare, protective services, ministry, and tech, I've watched change initiatives collapse for the same four reasons — every time:" },
      { type: "ul", items: [
        "The change is announced but never anchored — no one knows what to stop doing.",
        "The founder is the only person who understands why the change matters.",
        "Middle managers are asked to enforce the change without being coached through it themselves.",
        "There is no scoreboard, so people default to the old scoreboard.",
      ] },
      { type: "p", text: "None of those are strategy problems. They're leadership problems. And they're solvable." },
      { type: "h2", text: "The MAKECHANGE framework" },
      { type: "p", text: "MAKECHANGE is the ten-pillar operating system I use with CEOs, founders, and faith leaders: Momentum, Authority, Kingdom-Focus, Equity, Clarity, Healing, Action, Navigation, Growth, Empowerment. Each pillar answers a specific failure mode I've watched destroy otherwise good companies." },
      { type: "h3", text: "Start with Clarity, not communication" },
      { type: "p", text: "Most 'communication plans' are decoration on an unclear decision. Before you tell the org anything, write the change down as a single sentence a new hire could repeat in the break room. If you can't, the change isn't real yet." },
      { type: "h3", text: "Anchor Authority before you delegate" },
      { type: "p", text: "The people carrying the change need visible air cover. That means naming them, giving them explicit decision rights, and defending them in the first meeting where someone pushes back." },
      { type: "h3", text: "Build Momentum with a 30-day win" },
      { type: "p", text: "Pick something small, visible, and finishable inside 30 days. Ship it. Celebrate it publicly. The organization believes the change is real the moment it produces something concrete." },
      { type: "quote", text: "The world doesn't need more noise. It needs a voice — one that can lead the change instead of just announcing it.", cite: "Dr. Sully" },
      { type: "h2", text: "The move most executives skip: Healing" },
      { type: "p", text: "Change is loss. Even good change is loss. If you skip the grief — of the old identity, the old team shape, the old scoreboard — it comes back as passive resistance six months in. Trauma-informed leadership isn't soft. It's what actually keeps the change durable." },
      { type: "h2", text: "What to do this week" },
      { type: "ul", items: [
        "Write your change as one sentence. Read it out loud. Rewrite until it survives.",
        "Name the three people who will carry it. Give them public authority in the next all-hands.",
        "Pick the 30-day win. Put it on the wall.",
        "Book a working session with your leadership team to run this framework end-to-end.",
      ] },
      { type: "p", text: "If you want to run MAKECHANGE against a real change you're leading right now, that's what my private coaching engagements are built for. Bring the change, we'll pressure-test the plan, and you'll leave with a scoreboard your team can run without you in the room." },
      CTA_INQUIRE,
      { type: "p", text: "For the long-form version of this framework, the book Built On Purpose walks through each pillar with case studies from the executives, pastors, and founders I've coached through it." },
      CTA_BOOKS,
    ],
  },
  {
    slug: "trauma-informed-leadership-for-ceos",
    title: "Trauma-Informed Leadership: Why the Best CEOs Coach the Whole Human",
    description:
      "Trauma-informed leadership isn't therapy in a boardroom. It's the practical difference between a team that scales and one that quietly bleeds talent.",
    category: "Leadership",
    readMinutes: 8,
    publishedISO: "2026-07-15",
    eyebrow: "Leadership",
    hook: "The best operators I've coached don't manage performance. They manage nervous systems.",
    keywords: [
      "trauma-informed leadership",
      "psychological safety at work",
      "executive coaching for founders",
      "leadership development",
    ],
    body: [
      { type: "p", text: "The phrase 'trauma-informed' makes some executives flinch. They picture crying circles and productivity nosediving into a self-care spiral. That's not what this is." },
      { type: "p", text: "Trauma-informed leadership is a set of concrete, boardroom-appropriate skills for leading people whose nervous systems are already carrying load — from the pandemic, from layoffs, from the last founder who ran the place into the ground. Which is to say: every team." },
      { type: "h2", text: "The three assumptions that change everything" },
      { type: "ul", items: [
        "Behavior is data. When a normally sharp operator misses two deadlines, the deadline isn't the story.",
        "Safety precedes performance. Cortisol-soaked teams don't do their best thinking, no matter how talented.",
        "Regulation is contagious. Your team borrows your nervous system before it borrows your strategy.",
      ] },
      { type: "h2", text: "What it looks like in practice" },
      { type: "p", text: "A CEO I coach runs a fast-growing services firm. Six months in, his top account manager started missing details. The old playbook would be a PIP. Instead he asked three questions: 'What's changed for you this quarter? What are you carrying that I don't know about? What would give you 20% more oxygen?' Turns out her mother was in hospice. They rebuilt her book of business for the quarter. She stayed, she came back sharper than ever, and the story travels through that company as proof that this leader means what he says." },
      { type: "p", text: "That's trauma-informed leadership. It's not soft. It's expensive not to do it — because you replace people at $80k a pop instead of asking three questions." },
      { type: "h2", text: "Five skills to build this quarter" },
      { type: "h3", text: "1. Learn the difference between activation and character" },
      { type: "p", text: "The teammate who snapped in the meeting is not 'difficult.' They're activated. Different response required." },
      { type: "h3", text: "2. Regulate first, decide second" },
      { type: "p", text: "Don't send the Slack. Don't take the call. 90 seconds of breathwork is the highest-ROI investment in your leadership week." },
      { type: "h3", text: "3. Make repair a normal event" },
      { type: "p", text: "Leaders who apologize specifically and quickly build teams that take real risks. The apology is not the weakness; the avoidance is." },
      { type: "h3", text: "4. Name the elephant in the first 60 seconds" },
      { type: "p", text: "Every meeting after hard news that pretends the news didn't happen loses the team a little more. Name it. Move on." },
      { type: "h3", text: "5. Protect deep-work capacity like a fiduciary duty" },
      { type: "p", text: "You are paid to think clearly. The calendar that makes that impossible is a governance failure, not a productivity quirk." },
      { type: "quote", text: "Coach the human. The performance follows. Coach only the performance, and eventually you lose both." },
      { type: "h2", text: "Where to go from here" },
      { type: "p", text: "If you're a founder or CEO who wants to build this into your operating system — not as a values statement but as actual muscle — that's what a private coaching engagement is for. We work on the specific relationships, meetings, and decisions where trauma-informed leadership either shows up or doesn't." },
      CTA_INQUIRE,
      { type: "p", text: "For the framework version, the books cover the underlying psychology and the field-tested moves used by the CEOs, pastors, and boards I've coached." },
      CTA_BOOKS,
    ],
  },

  // ============================ SPEAKING ================================
  {
    slug: "how-to-book-a-keynote-speaker",
    title: "How to Book a Keynote Speaker Your Audience Will Actually Remember",
    description:
      "A practical guide for event planners and corporate teams: what to look for, what to ask, and how to book a keynote speaker without wasting your budget.",
    category: "Speaking",
    readMinutes: 6,
    publishedISO: "2026-07-15",
    eyebrow: "Speaking",
    hook: "Most keynote decisions are made off a highlight reel. That's why most keynotes are forgotten by Monday.",
    keywords: [
      "how to book a keynote speaker",
      "corporate keynote speaker",
      "book a keynote speaker",
      "keynote speaker booking guide",
    ],
    body: [
      { type: "p", text: "You've been handed a budget, a stage, and a mandate: find a keynote speaker who will make the room lean forward. Somewhere between the sizzle reels and the agency cold emails, the criteria go fuzzy — and you end up choosing on charisma instead of fit." },
      { type: "p", text: "Here's the process I recommend to the events teams that hire me — even when they ultimately hire someone else." },
      { type: "h2", text: "Step 1 — Define the after, not the topic" },
      { type: "p", text: "Bad brief: 'We need someone on AI.' Good brief: 'When people walk out of this keynote, we want them to believe our transformation is possible AND leave with three moves they can run on Monday.' The topic follows the after; not the other way around." },
      { type: "h2", text: "Step 2 — Vet substance, not stagecraft" },
      { type: "ul", items: [
        "Ask for a written point of view — a book, a long-form essay, a talk transcript. Speakers with real substance can hand you one in five minutes.",
        "Look for credentials that actually match the room — clinical, academic, operator, or all three.",
        "Ask what they've said 'no' to. Speakers who take every gig aren't curating a message.",
      ] },
      { type: "h2", text: "Step 3 — Ask the three questions no one asks" },
      { type: "ul", items: [
        "How will you customize this talk to our audience? (Watch for a real process, not 'I'll tweak the intro.')",
        "What's the ONE idea you want people quoting a week later? (If they can't answer in one sentence, the talk won't either.)",
        "What do you need from us to make this the best version of the talk? (Great speakers have a rider of intake, not just A/V.)",
      ] },
      { type: "h2", text: "Step 4 — Contract for the outcome, not the appearance" },
      { type: "p", text: "The best keynote engagements include a pre-event stakeholder call, a post-event asset (a resource, worksheet, or session recap the audience actually uses), and — for the top tier — a follow-through workshop 30 days later. That's how a keynote becomes a change event instead of a memory." },
      { type: "h2", text: "What a keynote from Dr. Sully looks like" },
      { type: "p", text: "For corporate conferences, universities, government convenings, and faith gatherings, my keynotes are built on the MAKECHANGE framework and customized to your audience through a 45-minute intake call. Every engagement includes a written pre-brief, on-stage delivery, and a post-event resource the room can use — not just remember." },
      { type: "p", text: "If you're evaluating speakers for a 2026 or 2027 event, the fastest path is to send over the brief and your target date. My team will send back availability, references from comparable rooms, and a customization plan within one business day." },
      CTA_INQUIRE,
      { type: "p", text: "For a preview of the frameworks I speak from, the books walk through the underlying material in long form — useful for your program committee and internal champions." },
      CTA_BOOKS,
    ],
  },
  {
    slug: "what-to-look-for-in-corporate-keynote-speaker",
    title: "What to Look For in a Corporate Keynote Speaker (Beyond the Sizzle Reel)",
    description:
      "The checklist for choosing a corporate keynote speaker who moves the room, respects the sponsors, and doesn't get forgotten by lunch.",
    category: "Speaking",
    readMinutes: 5,
    publishedISO: "2026-07-15",
    eyebrow: "Speaking",
    hook: "A great sizzle reel proves someone can present. It doesn't prove they can transform a room.",
    keywords: [
      "corporate keynote speaker",
      "best keynote speakers 2026",
      "hiring a keynote speaker",
      "AI keynote speaker",
    ],
    body: [
      { type: "p", text: "The sizzle reel is the wedding photo of the speaking world. Everyone looks great. Very little of it predicts what actually happens after the honeymoon. Here's the checklist I coach corporate events teams to run before they wire the deposit." },
      { type: "h2", text: "1. A written point of view" },
      { type: "p", text: "Every speaker worth their fee has a body of work — a book, a long-form article, a framework — that predates the talk. If the entire body of work is a highlight reel, you're paying for performance, not for substance." },
      { type: "h2", text: "2. Credentials that match the room" },
      { type: "p", text: "AI keynote for a board of hospital CEOs? You want operator credentials AND clinical fluency, not a coder who tweets. Leadership keynote for a faith-based nonprofit? You want someone who can speak both languages without code-switching mid-sentence." },
      { type: "h2", text: "3. A real customization process" },
      { type: "p", text: "'I'll adjust the examples' is not customization. What you want to hear: a stakeholder intake call, a review of your last three years of themes, and a written pre-brief before they finalize the deck." },
      { type: "h2", text: "4. References from comparable rooms" },
      { type: "p", text: "Ask for two references: one that looked like your audience, and one where the speaker was asked to stretch. Both matter." },
      { type: "h2", text: "5. A post-event artifact" },
      { type: "p", text: "The talk should leave the room with something concrete — a worksheet, a diagnostic, a chapter, a next step. The best keynotes are the ones your team is still quoting in the Q2 planning meeting." },
      { type: "h2", text: "6. A behavioral posture that fits your sponsors" },
      { type: "p", text: "Green room behavior travels. The speaker who is generous with your interns and thoughtful with your sponsors is the one you can invite back — and the one whose reputation attaches cleanly to your brand." },
      { type: "quote", text: "Book substance over spectacle. The room forgets the spectacle by Monday. The substance is what they quote a year later." },
      { type: "h2", text: "The Dr. Sully engagement model" },
      { type: "p", text: "Corporate keynote engagements include: (1) a 45-minute stakeholder intake, (2) a written pre-brief specific to your audience, (3) on-stage delivery of a talk built for the after you defined, and (4) a post-event resource — usually a MAKECHANGE-based worksheet or diagnostic your team can run internally." },
      CTA_INQUIRE,
      { type: "p", text: "If you'd like your program committee to preview the underlying frameworks, the books cover the material in long form and travel well as pre-read." },
      CTA_BOOKS,
    ],
  },

  // ============================== BOOKS ==================================
  {
    slug: "inside-built-on-purpose",
    title: "Inside Built On Purpose: What the Book Actually Teaches",
    description:
      "A guided tour of Built On Purpose — the MAKECHANGE framework in book form for CEOs, founders, and faith leaders building something that has to last.",
    category: "Books",
    readMinutes: 6,
    publishedISO: "2026-07-15",
    eyebrow: "Books",
    hook: "Built On Purpose is not a memoir with a chapter of tips at the end. It's the operating system I use in every coaching engagement.",
    keywords: [
      "Built On Purpose book",
      "best books for CEOs",
      "faith-based leadership books",
      "leadership books 2026",
    ],
    body: [
      { type: "p", text: "Most leadership books are 30 pages of insight surrounded by 200 pages of anecdote. Built On Purpose is the opposite. It's a working manual for leaders who are done borrowing frameworks from LinkedIn and want a full operating system — one they can run against a real change they're leading right now." },
      { type: "h2", text: "The premise" },
      { type: "p", text: "Every organization that outlasts its founder gets built on purpose — deliberately, in a specific order, using a small number of moves executed with unusual discipline. This book names those moves." },
      { type: "h2", text: "The ten pillars" },
      { type: "ul", items: [
        "Momentum — the compounding physics of small, visible wins",
        "Authority — how to give people real air cover, not just titles",
        "Kingdom-Focus — leading from mission, not from ego",
        "Equity — the operating system for fairness that actually scales",
        "Clarity — the discipline of writing decisions down until they survive",
        "Healing — why trauma-informed leadership is a scaling technology",
        "Action — the bias that separates operators from analysts",
        "Navigation — reading the terrain most executives miss",
        "Growth — building capacity in your people faster than the market builds pressure",
        "Empowerment — the exit criteria for every leader you build",
      ] },
      { type: "h2", text: "Who it's for" },
      { type: "p", text: "Founders past their first ten million, pastors leading through generational transitions, executives inheriting a team that was built for a different era, and board members who want a shared vocabulary with the CEOs they oversee." },
      { type: "h2", text: "How to read it" },
      { type: "p", text: "Not front to back. Pick the pillar that names the change you're leading right now. Read that chapter. Run the diagnostic at the end of it. Repeat weekly. Most of the CEOs I coach keep a copy open on the desk during their leadership team meeting." },
      { type: "quote", text: "The best leadership books are working manuals. Read them with a pen." },
      { type: "h2", text: "Where to start" },
      { type: "p", text: "The full catalog — Built On Purpose, the MAKECHANGE Playbook, and the companion ebooks — is on the books page. Every book stands alone, but they cross-reference; readers who go deep usually end up with two or three on the desk." },
      CTA_BOOKS,
      { type: "p", text: "If you'd rather run the framework with me directly against a specific change you're leading, that's what private coaching engagements are built for. Bring the change, we'll pressure-test the plan." },
      CTA_INQUIRE,
    ],
  },
  {
    slug: "best-books-for-faith-driven-ceos",
    title: "The Best Books for Faith-Driven CEOs (A Working Reading List)",
    description:
      "A curated, honest reading list for CEOs and founders leading with faith and rigor — plus where Built On Purpose fits in the stack.",
    category: "Books",
    readMinutes: 5,
    publishedISO: "2026-07-15",
    eyebrow: "Books",
    hook: "Most 'faith and business' books pick one side and lose the other. The good ones refuse that trade.",
    keywords: [
      "best books for CEOs",
      "faith-driven leadership books",
      "Christian CEO books",
      "leadership reading list",
    ],
    body: [
      { type: "p", text: "I get asked for this list constantly, usually by a founder in their late 30s who was raised in church, built a serious company, and now wants to lead it without splitting the two halves of their brain in half. This is the list I actually send." },
      { type: "h2", text: "The operator half" },
      { type: "ul", items: [
        "The Hard Thing About Hard Things — Ben Horowitz. Nothing else describes the loneliness of the CEO seat this honestly.",
        "High Output Management — Andy Grove. Old, unfashionable, and still the best book on running a team.",
        "The Score Takes Care of Itself — Bill Walsh. Culture and standards from someone who actually built them.",
      ] },
      { type: "h2", text: "The interior half" },
      { type: "ul", items: [
        "Emotionally Healthy Leadership — Peter Scazzero. Written for pastors; devastating for CEOs.",
        "Ruthless Elimination of Hurry — John Mark Comer. The book most operators need and none of them buy.",
        "Strengthening the Soul of Your Leadership — Ruth Haley Barton. Slow, useful, undervalued.",
      ] },
      { type: "h2", text: "The integration half" },
      { type: "ul", items: [
        "Built On Purpose — Dr. Sully. The MAKECHANGE framework in book form. Written specifically for leaders who refuse to split faith and business into different vocabularies.",
        "Half Time — Bob Buford. Still the best book on the second-half transition.",
      ] },
      { type: "h2", text: "How to actually use a list like this" },
      { type: "p", text: "Don't read three books at once. Pick one from the interior half and one from the operator half. Read them slowly, in parallel, over 90 days, with a pen. If a book doesn't earn a note in the first chapter, put it down without guilt." },
      { type: "quote", text: "The best CEOs I coach have three books on the desk and 30 they finally admitted they were never going to read. The three win." },
      { type: "h2", text: "Where to start with the Dr. Sully catalog" },
      { type: "p", text: "If you're new to my work, start with Built On Purpose for the framework, then the companion ebooks for the specific plays. The full stack — including bundle pricing — is on the books page." },
      CTA_BOOKS,
      { type: "p", text: "For leaders who want to run the framework against a real change instead of reading about it, private coaching is the direct path. Bring the change, we'll build the plan." },
      CTA_INQUIRE,
    ],
  },
  // ======================= FROM THE BOOKS (2026) ==========================
  {
    slug: "left-the-church-not-god-time-to-heal",
    title: "You Left the Church, Not God: Healing From Spiritual Abuse",
    description:
      "From Time to Heal: why church hurt is a real wound with a pattern, and how to start healing without walking away from your faith.",
    category: "Books",
    readMinutes: 6,
    publishedISO: "2026-09-26",
    eyebrow: "Time to Heal",
    hook: "You are not a backslider. You are a scattered sheep — and the Shepherd is already looking for you.",
    keywords: ["spiritual abuse recovery", "church hurt", "religious trauma", "RTSD", "Time to Heal book"],
    body: [
      { type: "p", text: "I have sat with too many people who told me the same thing in a hundred different ways: I still love God. I just can't walk back into that building. Strong people. Faithful people. People who gave a church their money, their time, their trust, their children — and got handed a wound with their own name blamed on it." },
      { type: "p", text: "The world told them to deconstruct their way out of faith. The church told them they were bitter. Almost nobody told them the truth. That is why I wrote Time to Heal." },
      { type: "h2", text: "Church hurt has a shape" },
      { type: "p", text: "My first close encounter with this wound was in a pastor's office. I watched a woman who had tithed, served, and prayed for her leaders for years get labeled rebellious because she named the abuse she suffered in that same house. She wasn't confused. She was traumatized by people who used Scripture as a weapon and titles as shields." },
      { type: "p", text: "Once I saw the pattern — the cycle, the silencing, the spiritual cost — I could not unsee it. I call it Religious Trauma Stress Disorder (RTSD). To be clear: RTSD is my framework for naming the wound, not a clinical diagnosis, and this teaching never replaces professional care." },
      { type: "h2", text: "Three truths to hold today" },
      { type: "ul", items: [
        "Leaving a building is not the same as leaving God.",
        "Naming a wound and healing a wound are partners, not rivals.",
        "Healing moves at the speed of safety. Go at your own pace.",
      ] },
      { type: "quote", text: "Is there no balm in Gilead; is there no physician there?", cite: "Jeremiah 8:22 (KJV)" },
      { type: "p", text: "If you are in crisis, call or text 988 in the U.S. You do not have to carry this alone." },
      CTA_BOOKS,
    ],
  },
  {
    slug: "honest-in-the-dark-fighting-for-joy",
    title: "Honest in the Dark: Fighting for Joy Without Faking It",
    description:
      "Why faithful believers still walk through dark seasons — and how to keep moving when heaven feels silent.",
    category: "Books",
    readMinutes: 6,
    publishedISO: "2026-09-26",
    eyebrow: "Honest in the Dark",
    hook: "This is not a happy book about a hard subject. It's an honest book about a real one.",
    keywords: ["faith and depression", "dark night of the soul", "Christian joy", "Honest in the Dark book"],
    body: [
      { type: "p", text: "Degrees do not exempt you from desolation. The anointing on your life does not guarantee you will feel it on a Tuesday when the weight is unbearable and the silence of heaven feels like rejection. Nobody told me that. Nobody told a lot of us that — and the silence has cost the church more than we are willing to count." },
      { type: "p", text: "I wrote Honest in the Dark because I was the man in the dark who needed it, and it did not exist." },
      { type: "h2", text: "Real darkness, real saints" },
      { type: "p", text: "Performance culture teaches believers to smile through desolation. Scripture doesn't. The saints of the Bible wept, questioned, and wrestled — and God did not leave them. You are not lost in the dark just because you can't see." },
      { type: "h2", text: "Move anyway" },
      { type: "ul", items: [
        "Name what is blocking your joy instead of pretending it isn't there.",
        "Watch for the trap of self-focus — isolation makes the dark darker.",
        "Take one faithful step, even when you can't feel it.",
        "Learn how to love someone else who is in the dark.",
      ] },
      { type: "quote", text: "This is not a performance. This is a fight. Stay honest. The light is coming.", cite: "Dr. Sully" },
      CTA_BOOKS,
    ],
  },
  {
    slug: "ceo-devotional-for-leaders-who-carry-payroll",
    title: "For the Ones Who Carry Payroll: A Devotional Word for Executives",
    description:
      "From CEO,000,000 — the 365-day devotional for executives who are called, equipped, and ordained to carry the weight of leadership.",
    category: "Leadership",
    readMinutes: 5,
    publishedISO: "2026-09-26",
    eyebrow: "CEO,000,000",
    hook: "This is not a book about getting rich. It's a book about the weight.",
    keywords: ["devotional for executives", "Christian CEO devotional", "faith and business leadership", "CEO,000,000"],
    body: [
      { type: "p", text: "You carry something most people around you do not understand. Payroll. People whose rent depends on your judgment. A decision you cannot discuss with your staff — and some nights, not even with your family." },
      { type: "p", text: "There are shelves of books promising that faith produces a bigger building, a bigger team, a bigger number. Most of them are lying to you. Somebody already did that to you, and it is part of why you are tired." },
      { type: "h2", text: "Called before you were qualified" },
      { type: "p", text: "CEO,000,000 is written for the ones who sign the front of the check, who lie awake over payroll and pray about it alone, who were called before they were ever qualified and carried it anyway. One day at a time, 365 days, it re-anchors the work to the One you actually serve." },
      { type: "quote", text: "Whatsoever ye do, do it heartily, as to the Lord, and not unto men.", cite: "Colossians 3:23 (KJV)" },
      { type: "h2", text: "A daily practice" },
      { type: "ul", items: [
        "Read one entry before you open email.",
        "Write the one decision you're carrying today.",
        "Pray over it before you delegate it.",
      ] },
      CTA_INQUIRE,
      CTA_BOOKS,
    ],
  },
  {
    slug: "stop-leading-from-broken-places",
    title: "Stop Leading From Broken Places: A Built On Purpose Primer",
    description:
      "Unhealed wounds show up in your leadership. Here's how the Built On Purpose framework helps leaders build from wholeness.",
    category: "Leadership",
    readMinutes: 5,
    publishedISO: "2026-09-26",
    eyebrow: "Built On Purpose",
    hook: "Your team doesn't follow your title. They follow your unhealed patterns.",
    keywords: ["trauma-informed leadership", "leading from wholeness", "Built On Purpose", "MAKECHANGE framework"],
    body: [
      { type: "p", text: "In two decades of coaching and consulting, I've seen the same thing over and over: brilliant leaders unknowingly building organizations around their own wounds. The control, the conflict avoidance, the overwork — it's rarely a skills gap. It's an unhealed place running the meeting." },
      { type: "h2", text: "What leading from a broken place looks like" },
      { type: "ul", items: [
        "You react to feedback as if it were rejection.",
        "You can't delegate because trust once cost you everything.",
        "Your calendar is full, but your purpose is foggy.",
      ] },
      { type: "h2", text: "Build on purpose" },
      { type: "p", text: "Built On Purpose pairs honest self-work with the MAKECHANGE Framework — Momentum, Authority, Kingdom-Focus, Equity, Clarity, Healing, Action, Navigation, Growth, Empowerment — so healing becomes a leadership strategy, not a side project." },
      { type: "quote", text: "If it doesn't make sense — make change.", cite: "Dr. Sully" },
      CTA_BOOKS,
      CTA_INQUIRE,
    ],
  },
];

export const BLOG_CATEGORIES: BlogPost["category"][] = ["Leadership", "Speaking", "Books"];

export const getPostBySlug = (slug: string): BlogPost | undefined =>
  BLOG_POSTS.find((p) => p.slug === slug);

export const getRelatedPosts = (slug: string, limit = 3): BlogPost[] => {
  const current = getPostBySlug(slug);
  if (!current) return BLOG_POSTS.slice(0, limit);

  const norm = (s: string) => s.toLowerCase().trim();
  const currentKeywords = new Set((current.keywords ?? []).map(norm));
  const currentTitleTokens = new Set(
    norm(current.title)
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length > 3),
  );

  // Score every other post: keyword overlap (3pts) + same category (2pts) +
  // shared title tokens (1pt each). Ties break by publish date (newer first).
  const scored = BLOG_POSTS.filter((p) => p.slug !== slug).map((p) => {
    let score = 0;
    for (const k of p.keywords ?? []) {
      if (currentKeywords.has(norm(k))) score += 3;
    }
    if (p.category === current.category) score += 2;
    for (const tok of norm(p.title).split(/[^a-z0-9]+/)) {
      if (tok.length > 3 && currentTitleTokens.has(tok)) score += 1;
    }
    return { post: p, score };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return b.post.publishedISO.localeCompare(a.post.publishedISO);
  });

  return scored.slice(0, limit).map((s) => s.post);
};

